import "../chart.scss";

import type { EChartsOption } from "echarts";
import { TooltipComponentOption } from "echarts";
import * as echarts from "echarts/core";
import type { CallbackDataParams } from "echarts/types/dist/shared";
import ReactEcharts from "echarts-for-react";
import { memo, useEffect, useRef } from "react";

import { NoDataView } from "../../NoDataView/NoDataView";
import ChartLabel from "../ChartLabel";
import { commonProperties, labelOption, tooltip } from "../constants";
import dark from "../themes/dark";
import light from "../themes/light";
import type { IChartProps } from "../types";

const initialOptions: EChartsOption = {
  ...commonProperties,
  tooltip: tooltip,
  series: [
    {
      barGap: 0,
      barWidth: "85%",
      color: "#A347F8",
      name: "Paid tokens",
      type: "bar",
      label: labelOption,
      showBackground: false,
      emphasis: {
        disabled: true,
      },
      data: [],
      stack: "x",
    },
    {
      legendHoverLink: true,
      color: "",
      name: "",
      type: "bar",
      label: labelOption,
      emphasis: {
        disabled: true,
      },
      barGap: 0,
      barWidth: "85%",
      data: [],
      stack: "x",
    },
  ],
};

const StackedChart: React.FC<IChartProps> = memo(
  ({ themeMode, height, chartData, isLoading }) => {
    const eChartsRef = useRef(null as any);
    echarts.registerTheme("dark", dark);
    echarts.registerTheme("light", light);

    useEffect(() => {
      if (!chartData.series.length) {
        return;
      }

      if (eChartsRef.current?.getEchartsInstance().getId()) {
        const chartId = eChartsRef.current?.getEchartsInstance().getId();
        document
          .querySelector(`[_echarts_instance_=${chartId}]`)
          ?.setAttribute("tabindex", "0");
      }
      // need to wait for the chart to be rendered before setting chart data
      setTimeout(() => {
        eChartsRef.current?.getEchartsInstance().setOption({
          aria: {
            enabled: true,
            label: {
              general: {
                withoutTitle: chartData.label.label + " chart",
              },
            },
          },
          tooltip: {
            formatter: (params: CallbackDataParams[]) => {
              const {
                reorderTooltipItems,
                yLabelFormatter,
                tooltipValueFormatter,
                tooltipTitles,
                sumLabel,
              } = chartData;

              const formatterFunction =
                tooltipValueFormatter || yLabelFormatter;
              let renderedParams = params.map((param: any, index: number) => {
                return {
                  ...param,
                  altColor: chartData.series[index].altColor,
                };
              });
              if (reorderTooltipItems) {
                renderedParams = renderedParams.reverse();
              }
              let totalDiv = "";
              if (sumLabel) {
                const sum = params.reduce(
                  (acc, curr) => acc + Number(curr.value),
                  0,
                );
                totalDiv = `
                <div class="balance-tooltip__item">
                  <div class="balance-tooltip__item__label">${sumLabel}</div>
                  <div class="balance-tooltip__item__value">
                    <b>${formatterFunction(sum)}</b>
                  </div>
                </div>`;
              }
              const { name } = params[0];
              return `<div>
              <div class="balance-tooltip__title">${
                tooltipTitles[params[0].dataIndex] || name
              }</div>
              <div class="balance-tooltip__content">
              ${totalDiv}
              ${renderedParams
                .map((param: any) => {
                  return `<div class="balance-tooltip__item">
                      <div class="balance-tooltip__item__label">${
                        param.seriesName
                      }</div>
                      <div class="balance-tooltip__item__value" style="color: ${
                        param.altColor || param.color
                      }">
                        <b>${formatterFunction(param.value)}
                        </b>
                      </div>
                    </div>`;
                })
                .join("")}
              </div>
            </div>`;
            },
          },
          yAxis: {
            axisLabel: {
              formatter: chartData.yLabelFormatter,
            },
          },
          xAxis: {
            data: chartData.xAxisData,
          },
          series: [
            {
              data: chartData.series[0].data,
              color: chartData.series[0].color,
              name: chartData.series[0].name,
            },
            {
              data: chartData.series[1].data,
              color: chartData.series[1].color,
              name: chartData.series[1].name,
            },
          ],
        });
      }, 150);
    }, [themeMode, chartData]);

    return (
      <div className="chart chart__wrapper">
        <ChartLabel
          title={chartData.label.label}
          data={isLoading ? [] : chartData.label.values}
        />
        {chartData.series.length === 0 || isLoading ? (
          <NoDataView isLoading={isLoading} />
        ) : (
          <ReactEcharts
            style={{ height }}
            echarts={echarts}
            option={initialOptions}
            opts={{ height: "auto" }}
            ref={eChartsRef}
            theme={themeMode}
            notMerge={true}
            lazyUpdate={true}
            onEvents={{
              mouseover: function (params: EChartsOption) {
                const updatedFree = chartData.series[0].data.map(
                  (value, index) => {
                    if (index !== params.dataIndex) {
                      return { value, itemStyle: { opacity: 0.2 } };
                    }
                    return value;
                  },
                );
                const updatedPaid = chartData.series[1].data.map(
                  (value, index) => {
                    if (index !== params.dataIndex) {
                      return { value, itemStyle: { opacity: 0.2 } };
                    }
                    return value;
                  },
                );
                eChartsRef.current?.getEchartsInstance().setOption({
                  series: [{ data: updatedFree }, { data: updatedPaid }],
                });
              },
              mouseout: function () {
                eChartsRef.current?.getEchartsInstance().setOption({
                  series: [
                    {
                      data: chartData.series[0].data,
                    },
                    {
                      data: chartData.series[1].data,
                    },
                  ],
                });
              },
            }}
          />
        )}
      </div>
    );
  },
);

export { StackedChart };
